import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent, PRIVACY_URL, type Consent } from "@/lib/consent";

/**
 * Bottom cookie-consent bar. Shows once across all Booqly frontends — see
 * `@/lib/consent` for the shared-cookie mechanism. Styled with shared tokens +
 * the local Button, so it matches whichever app it's mounted in.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (getConsent() !== null) return;
    setVisible(true);
    // Reveal on the next frame so the enter transition actually runs.
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!visible) return null;

  function choose(value: Consent) {
    setConsent(value);
    setEntered(false);
    window.setTimeout(() => setVisible(false), 250);
  }

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className={[
        "fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background",
        "shadow-[0_-10px_40px_-24px_rgba(10,10,10,0.25)]",
        "transition-all duration-300 ease-out motion-reduce:transition-none",
        entered ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
      ].join(" ")}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">Cookies</p>
          <p className="text-sm text-muted-foreground">
            We use essential cookies to keep you signed in, and optional ones to
            improve Booqly.{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-foreground underline underline-offset-4 hover:no-underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => choose("essential")}
            className="w-full sm:w-auto"
          >
            Essentials only
          </Button>
          <Button
            variant="default"
            onClick={() => choose("all")}
            className="w-full sm:w-auto"
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
